import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
  Badge, Flex, Select, Input, InputGroup, InputLeftElement, Icon,
  Button, HStack, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, FormControl,
  FormLabel, Textarea, useDisclosure, useToast, IconButton, Alert, AlertIcon,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { MdSearch, MdAdd, MdDownload, MdDelete, MdWarning } from 'react-icons/md';
import { getDocumentsByEmployee, uploadDocument, deleteDocument, downloadDocument, getExpiringDocuments } from '../../services/employee-document.service';
import httpClient from '../../utils/httpClient';

const DOC_TYPES = [
  { value: 'CI', label: 'Cédula de Identidad' },
  { value: 'CARNE_SALUD', label: 'Carné de Salud' },
  { value: 'TITULO', label: 'Título' },
  { value: 'CONTRATO', label: 'Contrato' },
  { value: 'CERTIFICADO', label: 'Certificado' },
  { value: 'OTRO', label: 'Otro' },
];

export default function EmployeeDocumentsView() {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const toast = useToast();

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [documents, setDocuments] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [form, setForm] = useState({ documentType: 'OTRO', title: '', expirationDate: '', notes: '', fileData: null, fileName: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadEmployees(); loadExpiring(); }, []);
  useEffect(() => { if (selectedEmployee) loadDocs(); }, [selectedEmployee]);

  const loadEmployees = async () => {
    try { const r = await httpClient.get('/employee'); setEmployees(r?.payload || r?.data?.payload || []); } catch (e) { console.error(e); }
  };

  const loadDocs = async () => {
    try { setLoading(true); const r = await getDocumentsByEmployee(selectedEmployee); setDocuments(r?.payload?.data || []); } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const loadExpiring = async () => {
    try { const r = await getExpiringDocuments(60); setExpiring(r?.payload?.data || []); } catch (e) { console.error(e); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setForm(prev => ({ ...prev, fileData: reader.result.split(',')[1], fileName: file.name })); };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!form.title || !selectedEmployee) { toast({ title: 'Completá título y seleccioná empleado', status: 'warning', duration: 3000 }); return; }
    try {
      setSaving(true);
      await uploadDocument({ employeeId: Number(selectedEmployee), documentType: form.documentType, title: form.title, fileData: form.fileData, fileName: form.fileName, expirationDate: form.expirationDate || null, notes: form.notes, createdBy: 1 });
      toast({ title: 'Documento subido', status: 'success', duration: 3000 }); onClose(); loadDocs();
    } catch (e) { toast({ title: 'Error', status: 'error', duration: 3000 }); } finally { setSaving(false); }
  };

  const handleDownload = async (id, name) => {
    try {
      const blob = await downloadDocument(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = name || 'documento'; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'Error al descargar', status: 'error', duration: 3000 }); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar documento?')) return;
    try { await deleteDocument(id); toast({ title: 'Eliminado', status: 'success', duration: 2000 }); loadDocs(); } catch (e) { toast({ title: 'Error', status: 'error' }); }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {expiring.length > 0 && (
        <Alert status="warning" borderRadius="md" mb={4}><AlertIcon />
          {expiring.length} documento(s) próximos a vencer en los próximos 60 días
        </Alert>
      )}
      <Card p={4}>
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
          <HStack spacing={3}>
            <Select w="300px" placeholder="Seleccionar empleado" value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)}>
              {employees.map(emp => {
                const name = emp.person ? `${emp.person.name} ${emp.person.lastName || emp.person.last_name || ''}` : `Emp #${emp.id}`;
                return <option key={emp.id} value={emp.id}>{name}</option>;
              })}
            </Select>
          </HStack>
          {selectedEmployee && <Button leftIcon={<MdAdd />} colorScheme="brand" onClick={() => { setForm({ documentType: 'OTRO', title: '', expirationDate: '', notes: '', fileData: null, fileName: '' }); onOpen(); }}>Subir documento</Button>}
        </Flex>
        {!selectedEmployee ? (
          <Flex justify="center" py={10}><Text color="gray.500">Seleccioná un empleado para ver su legajo</Text></Flex>
        ) : loading ? null : documents.length === 0 ? (
          <Flex justify="center" py={10}><Text color="gray.500">No hay documentos para este empleado</Text></Flex>
        ) : (
          <Box overflowX="auto"><Table size="sm" variant="simple"><Thead><Tr><Th borderColor={borderColor}>Tipo</Th><Th borderColor={borderColor}>Título</Th><Th borderColor={borderColor}>Archivo</Th><Th borderColor={borderColor}>Vencimiento</Th><Th borderColor={borderColor}>Fecha carga</Th><Th borderColor={borderColor}>Acciones</Th></Tr></Thead><Tbody>
            {documents.map(d => (
              <Tr key={d.id} _hover={{ bg: rowHover }}>
                <Td borderColor={borderColor}><Badge>{DOC_TYPES.find(t => t.value === d.document_type)?.label || d.document_type}</Badge></Td>
                <Td borderColor={borderColor}><Text fontWeight="600" fontSize="sm">{d.title}</Text></Td>
                <Td borderColor={borderColor}><Text fontSize="xs">{d.file_name || '-'}</Text></Td>
                <Td borderColor={borderColor}>{d.expiration_date ? <Text fontSize="sm" color={new Date(d.expiration_date) < new Date() ? 'red.500' : 'inherit'}>{new Date(d.expiration_date).toLocaleDateString('es-UY')}</Text> : '-'}</Td>
                <Td borderColor={borderColor}><Text fontSize="xs">{new Date(d.created_date).toLocaleDateString('es-UY')}</Text></Td>
                <Td borderColor={borderColor}><HStack spacing={1}>
                  {d.file_name && <IconButton size="xs" icon={<MdDownload />} variant="ghost" colorScheme="blue" onClick={() => handleDownload(d.id, d.file_name)} aria-label="Descargar" />}
                  <IconButton size="xs" icon={<MdDelete />} variant="ghost" colorScheme="red" onClick={() => handleDelete(d.id)} aria-label="Eliminar" />
                </HStack></Td>
              </Tr>
            ))}
          </Tbody></Table></Box>
        )}
      </Card>
      <Modal isOpen={isOpen} onClose={onClose} size="lg"><ModalOverlay /><ModalContent>
        <ModalHeader>Subir documento</ModalHeader><ModalCloseButton />
        <ModalBody pb={6}>
          <FormControl mb={3}><FormLabel>Tipo de documento</FormLabel><Select value={form.documentType} onChange={e => setForm({ ...form, documentType: e.target.value })}>
            {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select></FormControl>
          <FormControl mb={3} isRequired><FormLabel>Título</FormLabel><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ej: Carné de salud 2026" /></FormControl>
          <FormControl mb={3}><FormLabel>Archivo</FormLabel><Input type="file" onChange={handleFileChange} p={1} /></FormControl>
          <FormControl mb={3}><FormLabel>Fecha de vencimiento</FormLabel><Input type="date" value={form.expirationDate} onChange={e => setForm({ ...form, expirationDate: e.target.value })} /></FormControl>
          <FormControl><FormLabel>Notas</FormLabel><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></FormControl>
        </ModalBody>
        <ModalFooter><Button mr={3} onClick={onClose}>Cancelar</Button><Button colorScheme="brand" onClick={handleUpload}>Subir</Button></ModalFooter>
      </ModalContent></Modal>
    </Box>
  );
}
